import { z } from 'zod';

import {
  type FuelType,
  type Transmission,
  type VehicleListFilters,
  type VehicleSort,
} from '@/services/vehicles/types';
import { fuelLabels, transmissionLabels } from '@/utils/vehicleFormat';

import { BODY_CHOICE_KEYS, bodyChoiceFor, bodyTypesFor } from './bodyChoices';

/**
 * The one vehicle search definition, shared by the home search box, the /cars
 * filters and the URL (IA §3). Budget is not here yet: it waits on the
 * currency decision (A8).
 */

const FUELS = Object.keys(fuelLabels) as [FuelType, ...FuelType[]];
const TRANSMISSIONS = Object.keys(transmissionLabels) as [Transmission, ...Transmission[]];
export const SORTS = [
  'newest',
  'price_usd_asc',
  'price_usd_desc',
  'year_desc',
  'mileage_asc',
] as const satisfies readonly VehicleSort[];

export const sortLabels: Record<VehicleSort, string> = {
  newest: 'Newest listings',
  price_usd_asc: 'Price: low to high',
  price_usd_desc: 'Price: high to low',
  year_desc: 'Year: newest first',
  mileage_asc: 'Mileage: lowest first',
};

export const MIN_YEAR = 1990;
export const maxYear = () => new Date().getFullYear() + 1;

const slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const budget = z
  .string()
  .regex(/^\d{1,9}$/)
  .transform(Number)
  .pipe(z.number().int().positive());

/** Budget bands in USD (A8). Compared with each car's USD price. */
export const BUDGET_STEPS_USD = [5000, 10000, 15000, 20000, 30000, 50000, 75000, 100000] as const;
const year = z
  .string()
  .regex(/^\d{4}$/)
  .transform(Number)
  .pipe(z.number().int().min(MIN_YEAR).max(2100));

/** Form values: every field is a string, '' meaning "any". */
export const vehicleSearchFormSchema = z
  .object({
    make: z.union([z.literal(''), slug]),
    model: z.union([z.literal(''), slug]),
    yearMin: z.union([z.literal(''), year]),
    yearMax: z.union([z.literal(''), year]),
    budgetMax: z.union([z.literal(''), budget]),
    fuel: z.union([z.literal(''), z.enum(FUELS)]),
    transmission: z.union([z.literal(''), z.enum(TRANSMISSIONS)]),
    country: z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/)]),
    // A choice, not a body type: one of them stands for two (see bodyChoices).
    body: z.union([z.literal(''), z.enum(BODY_CHOICE_KEYS)]),
  })
  .refine((v) => v.yearMin === '' || v.yearMax === '' || v.yearMin <= v.yearMax, {
    path: ['yearMax'],
    message: '"Year to" must be the same as or later than "Year from".',
  });

export type VehicleSearchFormInput = z.input<typeof vehicleSearchFormSchema>;
export type VehicleSearchFormValues = z.output<typeof vehicleSearchFormSchema>;

export const emptySearchForm: VehicleSearchFormInput = {
  make: '',
  model: '',
  yearMin: '',
  yearMax: '',
  budgetMax: '',
  fuel: '',
  transmission: '',
  country: '',
  body: '',
};

const PARAM_KEYS = [
  'make',
  'model',
  'yearMin',
  'yearMax',
  // Omitted here once, which silently dropped the budget from every search
  // made through the form while hand-typed URLs still worked.
  'budgetMax',
  'fuel',
  'transmission',
  'country',
  'body',
] as const;

/**
 * URL → filters. Invalid or unknown values are dropped rather than failing the
 * page: a hand-edited or outdated link still shows results.
 */
export function filtersFromParams(params: URLSearchParams): VehicleListFilters {
  const pick = <T>(schema: z.ZodType<T>, value: string | null): T | undefined => {
    if (value === null || value === '') return undefined;
    const parsed = schema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
  };
  const filters: VehicleListFilters = {};
  const make = pick(slug, params.get('make'));
  const model = pick(slug, params.get('model'));
  const yearMin = pick(year, params.get('yearMin'));
  const yearMax = pick(year, params.get('yearMax'));
  const budgetMax = pick(budget, params.get('budgetMax'));
  const fuel = pick(z.enum(FUELS), params.get('fuel'));
  const transmission = pick(z.enum(TRANSMISSIONS), params.get('transmission'));
  const country = pick(z.string().regex(/^[A-Z]{2}$/), params.get('country'));
  const body = pick(z.enum(BODY_CHOICE_KEYS), params.get('body'));
  const sort = pick(z.enum(SORTS), params.get('sort'));
  const page = pick(z.coerce.number().int().min(1).max(1000), params.get('page'));
  if (make) filters.makeSlug = make;
  if (model) filters.modelSlug = model;
  if (yearMin !== undefined) filters.yearMin = yearMin;
  if (yearMax !== undefined) filters.yearMax = yearMax;
  if (budgetMax !== undefined) filters.budgetMaxUsd = budgetMax;
  if (fuel) filters.fuel = fuel;
  if (transmission) filters.transmission = transmission;
  if (country) filters.country = country;
  const bodyTypes = body ? bodyTypesFor(body) : undefined;
  if (bodyTypes) filters.bodyTypes = bodyTypes;
  if (sort) filters.sort = sort;
  if (page) filters.page = page;
  return filters;
}

/** Filters → form values (for pre-filling the form from the URL). */
export function formFromFilters(f: VehicleListFilters): VehicleSearchFormInput {
  return {
    make: f.makeSlug ?? '',
    model: f.modelSlug ?? '',
    yearMin: f.yearMin === undefined ? '' : String(f.yearMin),
    yearMax: f.yearMax === undefined ? '' : String(f.yearMax),
    budgetMax: f.budgetMaxUsd === undefined ? '' : String(f.budgetMaxUsd),
    fuel: f.fuel ?? '',
    transmission: f.transmission ?? '',
    country: f.country ?? '',
    body: bodyChoiceFor(f.bodyTypes),
  };
}

/** Validated form values → query string (without sort/page, which reset). */
export function paramsFromForm(values: VehicleSearchFormValues): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of PARAM_KEYS) {
    const value = values[key];
    if (value !== '') params.set(key, String(value));
  }
  return params;
}

/**
 * Where a search should land. A make-only search goes to the make's own page,
 * which is the better landing page for it (IA §3).
 */
export function searchDestination(values: VehicleSearchFormValues): string {
  const params = paramsFromForm(values);
  const keys = [...params.keys()];
  if (values.make !== '') {
    params.delete('make');
    if (values.model !== '') params.delete('model');
    const base =
      values.model !== '' ? `/cars/${values.make}/${values.model}` : `/cars/${values.make}`;
    const rest = params.toString();
    return rest ? `${base}?${rest}` : base;
  }
  return keys.length ? `/cars?${params.toString()}` : '/cars';
}
